import { CERITA_MAX_CHARS, DARURAT_CHECKED, DARURAT_CONTACTS, QURAN_CREDIT } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Copy, Flag, Info, Phone, SendHorizontal, ShieldCheck, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { Obj } from '../../components/Obj.tsx'
import { Sheet } from '../../components/Sheet.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { ApiError } from '../../lib/api.ts'
import { scrollToEl } from '../../lib/scroll.ts'
import { useToast } from '../../lib/toast.ts'
import { PassageBlock } from '../pegangan/PassageBlock.tsx'
import { CERITA_KEY, clearCerita, replyText, reportCerita, sendCerita, useCerita, type CeritaMessage, type CeritaState, type Quota } from './cerita.ts'

const EXAMPLES = ['Aku cemas soal rezeki bulan ini', 'Lagi kesal sama orang rumah', 'Capek banget sama kerjaan', 'Rasanya jauh dari Allah', 'Sedih kehilangan seseorang']

/** Why an answer is automatic, said plainly (docs/UX-PLAYBOOK.md §7.9). */
const REASON: Record<string, string> = {
  ai_off: 'Jawaban otomatis dari kartu Pegangan.',
  kuota: 'Jawaban otomatis dari kartu Pegangan (chat gratismu sudah habis).',
  ai_error: 'AI sedang tidak bisa dihubungi, jadi ini kartu Pegangan yang paling dekat.',
  ragu: 'Jawaban AI belum cukup yakin dengan sumbernya, jadi tidak ditampilkan. Ini kartu Pegangan yang paling dekat. Chat gratismu tidak terpakai.',
}

function QuotaNote({ q }: { q: Quota }) {
  if (!q.ai) return <p className="ct-quota">Jawaban diambil otomatis dari kartu Pegangan.</p>
  if (q.left === 0) return <p className="ct-quota">Chat gratismu sudah habis. Kamu masih bisa melanjutkan dengan jawaban otomatis dari kartu Pegangan.</p>
  return (
    <p className="ct-quota">
      <b>{q.left} chat gratis tersisa.</b> Jawaban AI diperiksa dulu ke sumbernya; kalau belum cukup yakin, kamu mendapat kartu Pegangan yang paling dekat.
    </p>
  )
}

const host = (url: string) => new URL(url).hostname.replace(/^www\./, '')

function Darurat() {
  return (
    <div className="ct-darurat">
      <ul>
        {DARURAT_CONTACTS.map((c) => (
          <li key={c.call}>
            <a className="btn btn-secondary btn-block" href={`tel:${c.call}`}><Phone size={18} aria-hidden="true" /> Telepon {c.call}</a>
            <p><b>{c.name}.</b> {c.how}.</p>
            <p className="help">
              {c.url && <><SourceLink href={c.url}>{host(c.url)}</SourceLink> · </>}
              Sumber: <SourceLink href={c.source}>{host(c.source)}</SourceLink>
            </p>
          </li>
        ))}
      </ul>
      <p className="help">Nomor diperiksa {DARURAT_CHECKED}. Kalau ada yang terluka, segera ke IGD rumah sakit terdekat.</p>
    </div>
  )
}

function Reply({ m, onCopy, onReport }: { m: CeritaMessage; onCopy: (m: CeritaMessage) => void; onReport: (m: CeritaMessage) => void }) {
  const r = m.reply
  return (
    <article className={`ct-reply${m.kind === 'darurat' ? ' urgent' : ''}`} aria-label="Jawaban Teman Cerita">
      <p className="ct-empati">{r.empati}</p>
      {m.kind === 'darurat' && <Darurat />}
      {m.kind === 'fikih' && m.source === 'llm' && <p className="pg-note" role="note"><Info size={16} aria-hidden="true" /> Untuk hukumnya, tanyakan langsung ke ustadz yang kamu percaya. Teman Cerita tidak memberi fatwa.</p>}
      {r.ayat && (
        <section className="ct-sec pg-ayat" aria-label="Ayat yang berkaitan">
          {m.kind === 'fikih' && <p className="muted">Ayat yang berkaitan dengan perasaanmu:</p>}
          <PassageBlock p={r.ayat} id={`ct-${m.id}`} what="ayat jawaban" />
        </section>
      )}
      {r.makna && <p className="ct-makna">{r.makna}</p>}
      {r.hadis && (
        <figure className="pg-hadis ct-sec">
          <span className="pill pill-mint">{r.hadis.grade}</span>
          <blockquote>{r.hadis.text}</blockquote>
          <figcaption>
            <span>{r.hadis.attribution}</span>
            <SourceLink href={r.hadis.url}>{r.hadis.source}</SourceLink>
          </figcaption>
          {r.hadis.makna && <p className="ct-makna">{r.hadis.makna}</p>}
        </figure>
      )}
      {r.langkah && <p className="ct-step"><span className="pg-when">Langkah kecil</span>{r.langkah}</p>}
      {r.pegangan && (
        <Link className="ct-card" to={`/pegangan/${r.pegangan.slug}`}>
          <Obj id="embun" size={40} />
          <span><small>Kartu Pegangan yang mungkin pas</small><b>{r.pegangan.judul}</b></span>
        </Link>
      )}
      <footer className="ct-foot">
        {r.reason && REASON[r.reason] && <p className="help">{REASON[r.reason]}</p>}
        {m.source === 'llm' && <p className="help"><ShieldCheck size={14} aria-hidden="true" /> Disusun AI dari ayat & hadis terpilih, lolos pemeriksaan sumber.</p>}
        {m.kind !== 'darurat' && (
          <p className="credit">
            Bahan renungan, bukan fatwa · {r.ayat ? <SourceLink href={r.ayat.url}>{QURAN_CREDIT}</SourceLink> : 'merujuk terjemah Kemenag RI'}
          </p>
        )}
        <div className="ct-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onCopy(m)}><Copy size={16} aria-hidden="true" /> Salin</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onReport(m)} disabled={m.reported}>
            <Flag size={16} aria-hidden="true" /> {m.reported ? 'Dilaporkan' : 'Lapor'}
          </button>
        </div>
      </footer>
    </article>
  )
}

/** /cerita: write what is on your heart, get a verse, its meaning and one small step (docs/PRD.md US-7.x). */
export function CeritaPage() {
  const { data, isPending, isError, refetch } = useCerita()
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [sending, setSending] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [reporting, setReporting] = useState<CeritaMessage | null>(null)
  const [reason, setReason] = useState('')
  const [clearing, setClearing] = useState(false)
  const [toast, show] = useToast()
  const end = useRef<HTMLDivElement>(null)
  const box = useRef<HTMLTextAreaElement>(null)

  const count = data?.messages.length ?? 0
  useEffect(() => {
    if (end.current) scrollToEl(end.current, { block: 'end' })
  }, [count, sending])

  // the box follows its text, also when it is emptied after sending or refilled after an error
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`
  }, [text])

  const send = async (e?: FormEvent) => {
    e?.preventDefault()
    const message = text.trim()
    if (message.length < 3 || sending) return
    setError('')
    setSending(message)
    setText('')
    try {
      const r = await sendCerita(message)
      qc.setQueryData<CeritaState>(CERITA_KEY, (s) => (s ? { ...s, messages: [...s.messages, r.message], quota: r.quota } : s))
      qc.setQueryData(['cerita', 'kuota'], { ok: true, quota: r.quota })
    } catch (err) {
      setText(message) // nothing is lost
      setError(err instanceof ApiError ? err.message : 'Belum bisa terkirim. Coba lagi.')
    } finally {
      setSending(null)
    }
  }
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends on a keyboard (desktop); on phones Enter is a new line
    if (e.key === 'Enter' && !e.shiftKey && window.matchMedia?.('(pointer: fine)').matches) {
      e.preventDefault()
      void send()
    }
  }

  const onCopy = async (m: CeritaMessage) => {
    try {
      await navigator.clipboard.writeText(replyText(m))
      show('Tersalin.')
    } catch {
      show('Belum bisa menyalin di perangkat ini.')
    }
  }
  const submitReport = async () => {
    if (!reporting) return
    try {
      await reportCerita(reporting.id, reason.trim())
      const id = reporting.id
      qc.setQueryData<CeritaState>(CERITA_KEY, (s) => (s ? { ...s, messages: s.messages.map((x) => (x.id === id ? { ...x, reported: true } : x)) } : s))
      setReporting(null)
      setReason('')
      show('Terima kasih, laporanmu akan kami tinjau.')
    } catch {
      show('Laporan belum terkirim. Coba lagi.')
    }
  }
  const clearAll = async () => {
    try {
      await clearCerita()
      qc.setQueryData<CeritaState>(CERITA_KEY, (s) => (s ? { ...s, messages: [] } : s))
      setClearing(false)
      show('Riwayat cerita sudah dihapus.')
    } catch {
      show('Belum bisa dihapus. Coba lagi.')
    }
  }

  return (
    <div className="page ct-page">
      <header className="ct-head">
        <Obj id="teko" size={56} />
        <div>
          <h1>Teman Cerita</h1>
          <p className="muted">Ceritakan apa saja. Jawabannya dari ayat Al-Qur’an dan satu langkah kecil.</p>
        </div>
        {count > 0 && (
          <button type="button" className="icon-btn plain" onClick={() => setClearing(true)} aria-label="Hapus riwayat cerita">
            <Trash2 size={20} aria-hidden="true" />
          </button>
        )}
      </header>

      {data && <QuotaNote q={data.quota} />}
      {data && <p className="help ct-privacy">Hindari menulis nama lengkap atau data pribadi. Urusan hukum fikih, tanyakan ke ustadz.</p>}

      {isPending && <SkCard label="Memuat cerita" lines={2} style={{ marginTop: 14 }} />}
      {isError && (
        <EmptyState obj="jam-pasir" title="Belum bisa dimuat" action={<button className="btn btn-secondary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}

      {data && count === 0 && !sending && (
        <div className="ct-empty">
          <p>Tulis saja apa yang ada di hatimu, tanpa perlu rapi.</p>
          <div className="ct-chips" role="group" aria-label="Contoh cerita">
            {EXAMPLES.map((x) => (
              <button key={x} type="button" className="chip-btn" onClick={() => { setText(x); box.current?.focus() }}>{x}</button>
            ))}
          </div>
        </div>
      )}

      <div className="ct-list" aria-live="polite">
        {data?.messages.map((m) => (
          <div key={m.id} className="ct-turn">
            <p className="ct-me">{m.message}</p>
            <Reply m={m} onCopy={onCopy} onReport={(x) => { setReporting(x); setReason('') }} />
          </div>
        ))}
        {sending && (
          <div className="ct-turn">
            <p className="ct-me">{sending}</p>
            <p className="ct-typing"><span className="dots" aria-hidden="true"><i /><i /><i /></span> Mencari ayat yang berkaitan dan memeriksa sumbernya…</p>
          </div>
        )}
        <div ref={end} />
      </div>

      {data && (
        <form className="ct-composer" onSubmit={send}>
          {error && <p className="error" role="alert">{error}</p>}
          <div className="ct-box">
            <label htmlFor="ct-text" className="sr">Ceritamu</label>
            <textarea
              id="ct-text"
              ref={box}
              rows={2}
              maxLength={CERITA_MAX_CHARS}
              placeholder="Apa yang sedang kamu rasakan?"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
            />
            <button type="submit" className="btn btn-primary ct-send" disabled={text.trim().length < 3 || !!sending} aria-label="Kirim cerita">
              <SendHorizontal size={20} aria-hidden="true" />
            </button>
          </div>
          {text.length > CERITA_MAX_CHARS - 200 && <p className="help" style={{ textAlign: 'right' }}>{text.length}/{CERITA_MAX_CHARS}</p>}
        </form>
      )}

      {reporting && (
        <Sheet title="Laporkan jawaban ini" onClose={() => setReporting(null)}>
          <p className="muted">Ada yang keliru atau kurang pas? Ceritakan singkat, tim kami akan meninjaunya.</p>
          <label htmlFor="ct-reason" className="sr">Alasan</label>
          <textarea id="ct-reason" className="input ct-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: ayatnya kurang sesuai dengan ceritaku" />
          <button type="button" className="btn btn-secondary btn-block" onClick={submitReport} style={{ marginTop: 12 }}>Kirim laporan</button>
        </Sheet>
      )}
      {clearing && (
        <Sheet title="Hapus semua cerita?" onClose={() => setClearing(false)}>
          <p className="muted">Semua cerita dan jawabannya di akun ini akan dihapus dan tidak bisa dikembalikan. Chat gratis yang sudah terpakai tidak kembali.</p>
          <button type="button" className="btn btn-danger btn-block" onClick={clearAll} style={{ marginTop: 14 }}>Hapus semua</button>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setClearing(false)} style={{ marginTop: 8 }}>Batal</button>
        </Sheet>
      )}

      <div className="toast-slot" role="status">{toast && <p className="toast">{toast}</p>}</div>
    </div>
  )
}
